<script setup>
import { computed, ref } from "vue";
import { RouterLink } from "vue-router";
import { sendMetrik } from "@/utils/metrika";
import { useRequestsStore } from "@/stores/storeRequests";
import { useSettingsStore } from "@/stores/storeSettings";
import { useQuizWordsStore } from "@/stores/storeQuizWords";
import { useQuizMatchesStore } from "@/stores/storeQuizMatches";
import { formatDate } from "@/utils/formatter";
import gameMatchesImg from "@/assets/img/games/game_matches.webp";
import gameWordsImg from "@/assets/img/games/game_words.webp";

const storeRequests = useRequestsStore();
const storeSettings = useSettingsStore();
const storeQuizMatches = useQuizMatchesStore();
const storeQuizWords = useQuizWordsStore();

const isTextInfo = ref(null);
let timer = null;

const showInfo = (id) => {
	isTextInfo.value = id;
	clearTimeout(timer);
	timer = setTimeout(() => {
		isTextInfo.value = null;
	}, 3000);
};

const hideInfo = () => {
	isTextInfo.value = null;
	clearTimeout(timer);
};

const games = computed(() => [
	{
		id: 1,
		image: gameMatchesImg,
		title: "Игра «3 в ряд»",
		desc: "Собирайте комбинации и получайте подарок",
		link: "/quiz_matches",
		isGame: storeQuizMatches.isMatchGameAvailable, // !storeRequests.isApprovedChecksCount
		textError: storeSettings.statusStockActive ? "Игра доступна только новым участникам до подтверждения первого чека." : `Игра доступна только в период проведения акции: с ${formatDate(storeSettings.startDate)} по ${formatDate(storeSettings.endDate)}.`,

		goal: "click_3_in_a_row_main",
	},
	{
		id: 2,
		image: gameWordsImg,
		title: "Игра «Листопад»",
		desc: "Составляйте слова из букв, проходите уровни и получайте листочки",
		link: "/quiz_words",
		isGame: storeQuizWords.isWordsGameAvailable, // storeRequests.userData?.is_leaf_fall_game_available
		textError: storeSettings.statusStockActive ? "Игра доступна зарегистрированным пользователям с принятым чеком" : `Игра доступна только в период проведения акции: с ${formatDate(storeSettings.startDate)} по ${formatDate(storeSettings.endDate)}.`,
		goal: "click_leaf_fall_main",
	},
]);
</script>
<template>
	<section class="games" id="games">
		<div class="container">
			<h2 class="games__title title2">
				Играйте в интерактивы на сайте, <br />
				получайте еще больше листочков <br class="br-mobile" /> и подарков
			</h2>
			<div class="games__container">
				<div class="game" v-for="game in games" :key="game.id">
					<div class="game__image">
						<img class="full-img" width="236" height="236" :src="game.image" alt="изображение" />
					</div>
					<div class="game__content">
						<h3 class="game__title title3">{{ game.title }}</h3>
						<p class="game__desc">{{ game.desc }}</p>
						<RouterLink @click="sendMetrik('reachGoal', game.goal)" v-if="game.isGame" :to="game.link" class="game__btn btn --small">Играть</RouterLink>
						<button v-else class="game-btn-disabled" @mouseenter="showInfo(game.id)" @click="showInfo(game.id)">
							игра недоступна
							<svg width="20" height="20"><use href="#question"></use></svg>
							<Transition>
								<span class="game-info-text" v-if="isTextInfo === game.id">
									<svg width="20" height="20" @click.stop="hideInfo"><use href="#x"></use></svg>
									{{ game.textError }}
								</span>
							</Transition>
						</button>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";

.games {
	position: relative;
	padding: 6.5rem 0 14.5rem;
	@media (max-width: vars.$m) {
		padding: 3rem 0 9rem;
	}
	&::before {
		position: absolute;
		content: "";
		background-image: url("@/assets/img/games/games_bg.webp");
		background-repeat: no-repeat;
		background-position: center; /* x y*/
		background-size: auto; /* w h*/
		width: 105.6rem;
		height: 79.3rem;
		top: 0;
		left: 0;
		z-index: -1;
		@media (max-width: vars.$xx) {
			display: none;
		}
	}
	

	&__title {
		margin-bottom: 4rem;
		text-align: center;

		@media (max-width: vars.$m) {
			margin-bottom: 2.4rem;
		}
		@media (max-width: vars.$x480) {
			    max-width: 30rem;
				margin-left: auto;
				margin-right: auto;
				text-align: center;
		}
		.br-mobile {
			display: none;
			@media (max-width: vars.$x480) {
				display: block;
			}
		}
		br {
			@media (max-width: vars.$mn) {
				display: none;
			}
		}
	}

	&__container {
		display: flex;
		justify-content: space-between;
		gap: 4rem;

		@media (max-width: vars.$t) {
			flex-direction: column;
			gap: 4rem;
		}
	}
}

.game {
	display: flex;
	gap: 3.2rem;
	max-width: 67.5rem;
	width: 100%;

	@media (max-width: vars.$m) {
		gap: 1.6rem;
		max-width: 100%;
	}
	@media (max-width: vars.$x480) {
		align-items: start;
	}

	&__image {
		width: 23.6rem;
		height: 23.6rem;
		flex-shrink: 0;
		border-radius: 4rem;
		overflow: hidden;
		background: rgba(255, 255, 255, 0.01);
		border: 1px solid var(--border-accent-2-3);
		backdrop-filter: blur(7.7px);
		box-shadow: inset 0 1.3rem 3.48rem 0 rgba(255, 255, 255, 0.14);

		@media (max-width: vars.$x480) {
			width: 8rem;
			height: 8rem;
			border-radius: 1.6rem;
			backdrop-filter: blur(2.61px);
			box-shadow: inset 0 0.44rem 1.18rem 0 rgba(255, 255, 255, 0.14);
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 1.5rem;
		padding-top: 3.2rem;
		@media (max-width: vars.$xx) {
			gap: 0.6rem;
			padding-top: 1rem;
		}
	}

	&__title {
		// font-family: var(--second-family);
		// font-weight: 500;
		// font-size: 1.8rem;
		// line-height: 140%;
		// text-transform: uppercase;
		// color: var(--text-accent-2-100);

		// @media (max-width: vars.$xx) {
		// 	font-size: 1.6rem;
		// }
	}

	&__desc {
		font-family: var(--font-family);
		font-weight: 500;
		font-size: 2rem;
		line-height: 120%;
		color: var(--text-inverse-100);

		@media (max-width: vars.$xx) {
			font-size: 1.4rem;
			line-height: 140%;
		}
	}
	&__btn {
		@media (max-width: vars.$x480) {
			min-height: 3.2rem;
		font-size: 1.4rem;
		}
	}
	// &__btn {
	// 	&.--disabled {
	// 		position: relative;
	// 		display: flex;
	// 		align-items: center;
	// 		gap: 1rem;
	// 		background: transparent;
	// 		border: none;
	// 		border-bottom: 1px dashed var(--border-accent-2-1);
	// 		padding: 0 0 0.8rem 0;
	// 		height: auto;
	// 		border-radius: 0;
	// 		font-family: var(--second-family);
	// 		font-weight: 700;
	// 		font-size: 2rem;
	// 		line-height: 100%;
	// 		text-transform: uppercase;
	// 		color: var(--text-inverse-100);
	// 		@media (max-width: vars.$m) {
	// 			font-size: 1.8rem;
	// 		}

	// 		@media (hover: hover) {
	// 			&:hover {
	// 				border-color: transparent;
	// 			}
	// 		}

	// 		svg {
	// 			width: 2rem;
	// 			height: 2rem;
	// 			fill: var(--border-accent-2-1);
	// 		}
	// 	}
	// }
	// &__info-text {
	// 	position: absolute;
	// 	top: 35px;
	// 	left: 0;
	// 	font-family: var(--font-family);
	// 	font-weight: 400;
	// 	font-size: 1.6rem;
	// 	line-height: 140%;
	// 	color: var(--text-inverse-100);
	// 	text-transform: none;
	// 	border-radius: 2.4rem;
	// 	padding: 1.6rem 1.6rem 1.2rem;
	// 	width: 40rem;
	// 	@extend .base-bg;
	// 	@media (max-width: vars.$xx) {
	// 		transform: translateX(-50%);
    // 		left: 50%;
	// 	}	
	// 	@media (max-width: vars.$x480) {
	// 		width: 30rem;
	// 	}	

	// 	svg {
	// 		fill: #fff;
	// 		position: absolute;
	// 		top: 5px;
	// 		right: 10px;
	// 	}
	// }
}

.v-enter-active,
.v-leave-active {
	transition: opacity 0.8s ease, transform 0.8s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	transform: translateY(10%);
}
</style>
